import type { ReactNode } from 'react';

/**
 * Badge — 小型ラベル / counter (Sprint BA、docs/16 §10-2 残 component)。
 *
 * 用途:
 *   - 投稿一覧の status (公開 / 下書き / 非公開) 表示
 *   - 通知未読件数
 *   - admin ダッシュボードの category tag 等
 *
 * variant は意味的に Modal / Alert と揃え、`bg-{variant}-100 text-{variant}-700` で
 * 低彩度のコントラスト (WCAG AA 相当) を確保。
 */

export type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  className?: string;
}

const VARIANT_CLASS: Record<BadgeVariant, string> = {
  default: 'bg-slate-100 text-slate-700',
  primary: 'bg-primary-100 text-primary-700',
  success: 'bg-success-100 text-success-700',
  warning: 'bg-warning-100 text-warning-700',
  danger: 'bg-danger-100 text-danger-700',
};

const SIZE_CLASS: Record<BadgeSize, string> = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-0.5 text-sm',
};

export function Badge({ children, variant = 'default', size = 'sm', className = '' }: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${className}`.trim()}
      data-variant={variant}
    >
      {children}
    </span>
  );
}
